import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useRef } from "react";
import { api, type RequestDetail as Detail } from "./api";
import { ContentView } from "./ContentView";
import { money, timestamp, tokens } from "./format";
import { useForgeEvent } from "./hooks";

const ms = (n: number | null) => (n === null ? "–" : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`);
const shortAlias = (a: string | null, id: string) => (a ?? `…${id.slice(-8)}`).replace(/^[^:]*:/, "").replace(/~[a-z0-9]+$/, "");

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="stack gap-xsmall">
      <h3 className="forge-typography--heading2 card-title">{title}</h3>
      <dl className="details forge-typography--body2">{children}</dl>
    </section>
  );
}
const Row = ({ k, v }: { k: string; v: ReactNode }) => (<><dt>{k}</dt><dd>{v}</dd></>);

export type PanelTab = "details" | "sent";

/** Whether the signed-in person may open this request's content: their own, with a full record. */
export function useCanSeeContent(d: Detail | undefined): boolean {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  return !!d && !!me.data && !!d.userId && me.data.user.userId === d.userId && d.auditRecordRetained;
}

/**
 * One request in a panel: its details and, on the person's own requests, a second tab with what was
 * sent (TAI-2392). The tab is the caller's state, so stepping to the next request keeps it. Content
 * loads only while that tab is open.
 */
export function RequestPanel({ d, tab, onTab, nav }: { d: Detail; tab: PanelTab; onTab: (t: PanelTab) => void; nav?: ReactNode }) {
  const canSee = useCanSeeContent(d);
  const active: PanelTab = canSee ? tab : "details";
  return (
    <div className="stack gap-medium">
      {canSee && <PanelTabs tab={active} onTab={onTab} />}
      {active === "sent" && nav}
      {active === "sent" ? <ContentView key={d.requestId} requestId={d.requestId} /> : <RequestDetailView d={d} />}
    </div>
  );
}

function PanelTabs({ tab, onTab }: { tab: PanelTab; onTab: (t: PanelTab) => void }) {
  const bar = useRef<HTMLElement>(null);
  useForgeEvent(bar, "forge-tab-bar-change", (e: CustomEvent<{ index: number }>) => onTab(e.detail.index === 1 ? "sent" : "details"));
  return (
    <forge-tab-bar ref={bar} active-tab={tab === "sent" ? "1" : "0"}>
      <forge-tab>Details</forge-tab>
      <forge-tab>What was sent</forge-tab>
    </forge-tab-bar>
  );
}

/** One request, everything the spend log holds. What was said is the other tab, for its owner only. */
export function RequestDetailView({ d }: { d: Detail }) {
  const inputTotal = d.tokens.text + d.tokens.cacheRead + d.tokens.cacheWrite;
  const own = useCanSeeContent(d);
  return (
    <div className="stack gap-large">
      <div className="stack gap-xsmall">
        <div className="row gap-small">
          <span className={`status-dot ${d.status === "success" ? "status-dot--ok" : "status-dot--fail"}`} />
          <h2 className="forge-typography--heading4 card-title">{d.model}</h2>
        </div>
        <span className="muted">{timestamp(d.startTime)} · {d.tool ?? "unknown tool"} · key {shortAlias(d.keyAlias, d.keyId)}</span>
      </div>

      {d.error && (
        <forge-inline-message theme="error">
          <forge-icon slot="icon" name="error" />
          <div slot="title">{d.error.type ?? "Request failed"}{d.error.code ? ` (${d.error.code})` : ""}</div>
          <span>{d.error.message ?? "No message recorded."}</span>
        </forge-inline-message>
      )}

      <Section title="Where it went">
        <Row k="Asked for" v={d.model} />
        <Row k="Served by" v={<span className="mono">{d.deployment ?? "–"}</span>} />
        <Row k="Provider" v={`${d.provider ?? "–"}${d.region ? ` · ${d.region}` : ""}`} />
        <Row k="API" v={<span className="mono">{d.callType ?? "–"}</span>} />
        <Row k="Response cache" v={d.cacheHit ? "Hit: served from cache, not billed" : "Miss"} />
        {(d.retries > 0 || d.fallbacks > 0) && <Row k="Retries / fallbacks" v={`${d.retries} / ${d.fallbacks}`} />}
      </Section>

      <Section title="Timing">
        <Row k="First token" v={ms(d.ttftMs)} />
        <Row k="Total" v={ms(d.durationMs)} />
      </Section>

      <Section title="Tokens">
        <Row k="Input, total" v={tokens(inputTotal)} />
        <Row k="· new text" v={tokens(d.tokens.text)} />
        <Row k="· read from prompt cache" v={tokens(d.tokens.cacheRead)} />
        <Row k="· written to prompt cache" v={
          d.tokens.cacheWrite1h || d.tokens.cacheWrite5m
            ? `${tokens(d.tokens.cacheWrite)} (1 h: ${tokens(d.tokens.cacheWrite1h)}, 5 min: ${tokens(d.tokens.cacheWrite5m)})`
            : tokens(d.tokens.cacheWrite)} />
        <Row k="Output" v={tokens(d.tokens.output)} />
        {d.tokens.reasoning > 0 && <Row k="· of which reasoning" v={tokens(d.tokens.reasoning)} />}
      </Section>

      <Section title="Estimated cost">
        <Row k="Input" v={money(d.cost.input)} />
        {d.cost.cacheRead > 0 && <Row k="· cache read" v={money(d.cost.cacheRead)} />}
        {d.cost.cacheWrite > 0 && <Row k="· cache write" v={money(d.cost.cacheWrite)} />}
        <Row k="Output" v={money(d.cost.output)} />
        {d.cost.reasoning > 0 && <Row k="· reasoning" v={money(d.cost.reasoning)} />}
        {d.cost.tool > 0 && <Row k="Tools (e.g. web search)" v={money(d.cost.tool)} />}
        <Row k="Total" v={<b>{d.cacheHit ? "$0 (cache hit)" : money(d.cost.total)}</b>} />
      </Section>

      <Section title="Client">
        <Row k="User agent" v={<span className="mono wrap-anywhere">{d.userAgent ?? "–"}</span>} />
        <Row k="From address" v={<span className="mono">{d.requesterIp ?? "–"}</span>} />
        {d.requestTags.length > 0 && <Row k="Tags" v={<span className="mono wrap-anywhere">{d.requestTags.join(", ")}</span>} />}
        <Row k="Request id" v={<span className="mono wrap-anywhere">{d.requestId}</span>} />
        {d.sessionId && <Row k="Session" v={<span className="mono wrap-anywhere">{d.sessionId}</span>} />}
      </Section>

      <p className="forge-typography--body1 muted">
        {!d.auditRecordRetained
          ? "The gateway kept no full record of this request."
          : own
            ? "The full prompt and response are kept in the gateway's audit store. Only you can view them, in What was sent."
            : "The full prompt and response are kept in the gateway's audit store. Only the person who sent this request can view them."}
        {" "}The address is the network egress point (VPN or proxy), not necessarily your machine.
      </p>
    </div>
  );
}
